import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import Nav from "@/components/welp/Nav";
import PageBg from "@/components/welp/PageBg";
import { Check, ArrowRight } from "lucide-react";

const KNOWS = ["Yes", "No"];
const ACC = ["Accurate", "Partially Accurate", "Does Not Reflect My Experience"];
const TIME = ["I had a blast", "Could've been better", "Disaster"];
const SHOT = ["Absolutely", "It's possible", "That's not the best idea"];

function Pill({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`px-5 py-3 rounded-full border transition text-sm font-medium ${active ? "bg-[#FF1A82] text-white border-[#FF1A82] glow-lime" : "bg-black/5 text-[#3A3A3A] border-black/10 hover:border-[#10B981]/60 hover:text-[#10B981]"}`}>
      {children}
    </button>
  );
}

function Q({ label, children }) {
  return (
    <div className="space-y-3">
      <div className="text-xs uppercase tracking-[0.22em] text-[#10B981]">{label}</div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

export default function ResponsePage() {
  const { reviewId } = useParams();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [reviewInfo, setReviewInfo] = useState(null);
  const [d, setD] = useState({
    knows_person: "", accuracy: "", time_together: "", another_shot: "",
    last_words: "", apology: false,
  });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav(`/login?next=/respond/${reviewId}`, { replace: true }); return; }
    api.getReview(reviewId).then(setReviewInfo).catch(() => toast.error("Review not found"));
  }, [user, loading, nav, reviewId]);

  if (loading) return <PageBg><Nav /><div className="p-20 text-center text-[#3A3A3A] font-semibold">Checking your session…</div></PageBg>;
  if (!user) return null;

  const set = (k, v) => setD(o => ({ ...o, [k]: v }));
  const ok = d.knows_person && d.accuracy && d.time_together && d.another_shot && d.last_words.trim();

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.submitResponse({ review_id: reviewId, ...d });
      toast.success("Response submitted");
      setSubmitted(true);
      setD({ knows_person: "", accuracy: "", time_together: "", another_shot: "", last_words: "", apology: false });
    } catch (err) {
      const det = err?.response?.data?.detail;
      toast.error(typeof det === "object" ? det.message : (det || "Response failed"));
    } finally { setBusy(false); }
  };

  const startReciprocal = () => {
    if (!reviewInfo?.reviewer?.display_name) return;
    // Prefill the review wizard with the reviewer's display name as the username, platform "Welp"
    nav(`/review/new?u=${encodeURIComponent(reviewInfo.reviewer.display_name)}&p=${encodeURIComponent("Welp")}`);
  };

  return (
    <PageBg>
      <Nav />
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-16">
        <div className="text-veil">
        <div className="text-xs uppercase tracking-[0.32em] mb-3">
          <span className="text-[#10B981]">Welp</span> <span className="text-[#5A5A5A]">·</span> <span className="text-[#FF1A82]">Dating With Integrity</span> <span className="text-[#5A5A5A]">·</span> Your response
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl text-[#1A1A1A]">Your side of <span className="italic text-[#FF1A82]">the story.</span></h1>
        </div>

        {!submitted ? (
          <form onSubmit={submit} className="mt-10 rounded-3xl bg-white/70 backdrop-blur-lg shadow-lg border border-black/10 p-8 space-y-7 animate-fade-up">
            <Q label="Do you know this person?">{KNOWS.map(o => <Pill key={o} active={d.knows_person === o} onClick={() => set("knows_person", o)} testid={`know-${o}`}>{o}</Pill>)}</Q>
            <Q label="How does this review feel?">{ACC.map(o => <Pill key={o} active={d.accuracy === o} onClick={() => set("accuracy", o)} testid={`acc-${o}`}>{o}</Pill>)}</Q>
            <Q label="Did your time together go well?">{TIME.map(o => <Pill key={o} active={d.time_together === o} onClick={() => set("time_together", o)} testid={`time-${o}`}>{o}</Pill>)}</Q>
            <Q label="Would you be willing to give it another shot?">{SHOT.map(o => <Pill key={o} active={d.another_shot === o} onClick={() => set("another_shot", o)} testid={`shot-${o}`}>{o}</Pill>)}</Q>
            <div className="space-y-3">
              <div className="text-xs uppercase tracking-[0.22em] text-[#10B981]">Any last words? (max 300)</div>
              <textarea data-testid="last-words" maxLength={300} value={d.last_words} onChange={e => set("last_words", e.target.value)} rows={4}
                className="w-full bg-black/5 border border-black/15 rounded-2xl px-5 py-3 text-[#1A1A1A] outline-none focus:border-[#FF1A82] resize-none" />
              <div className="text-xs text-[#5A5A5A] text-right">{d.last_words.length}/300</div>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input data-testid="resp-apology" type="checkbox" checked={d.apology} onChange={e => set("apology", e.target.checked)} className="mt-1 w-5 h-5 accent-neon-lime" />
              <span className="text-[#1A1A1A]">I would like to apologize for my part in this experience.</span>
            </label>
            <button disabled={!ok || busy} data-testid="resp-submit" className="px-7 py-4 rounded-full bg-[#FF1A82] text-white font-semibold glow-lime hover:brightness-110 transition disabled:opacity-40 disabled:shadow-none">
              {busy ? "Submitting…" : "Submit response"}
            </button>
          </form>
        ) : (
          <div className="mt-10 rounded-3xl bg-white/70 backdrop-blur-lg shadow-lg border border-[#FF1A82]/50 glow-lime p-8 animate-fade-up" data-testid="response-success">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#FF1A82] mb-3">
              <Check className="w-4 h-4" /> Response saved
            </div>
            <h2 className="font-heading text-3xl md:text-4xl text-[#1A1A1A]">Thanks for showing up.</h2>
            <p className="text-[#3A3A3A] mt-3">Your side of the story is now part of <span className="text-[#10B981]">The Skinny</span>. Reviewers and searchers will see your perspective alongside the original review.</p>

            <div className="mt-8 rounded-2xl bg-gray-100 border border-[#10B981]/40 p-6">
              <div className="text-xs uppercase tracking-[0.22em] text-[#10B981] mb-2">Want to share your side too?</div>
              <h3 className="font-heading text-2xl text-[#1A1A1A]">
                Leave a review of <span className="italic text-[#FF1A82]">{reviewInfo?.reviewer?.display_name || "the original reviewer"}</span>
              </h3>
              <p className="text-[#3A3A3A] mt-2 text-sm">Accountability goes both ways. Submit your own honest experience of how the date went.</p>
              <div className="flex flex-wrap gap-3 mt-5">
                <button onClick={startReciprocal} data-testid="reciprocal-review-btn"
                  className="px-6 py-3 rounded-full bg-[#10B981] text-white font-medium glow-pink hover:brightness-110 transition inline-flex items-center gap-2">
                  Write reciprocal review <ArrowRight className="w-4 h-4" />
                </button>
                <button onClick={() => nav(-1)} data-testid="back-to-skinny"
                  className="px-6 py-3 rounded-full border border-black/15 text-[#3A3A3A] hover:border-[#FF1A82] hover:text-[#FF1A82] transition">
                  Back to The Skinny
                </button>
              </div>
            </div>

            <div className="mt-6 rounded-2xl bg-[#FF1A82]/10 border-2 border-[#FF1A82] p-6">
              <div className="text-xs uppercase tracking-[0.22em] text-[#FF1A82] mb-2">Curious about the stars?</div>
              <h3 className="font-heading text-2xl text-[#1A1A1A]">Try a <span className="italic text-[#FF1A82]">zodiac compatibility</span> test.</h3>
              <p className="text-[#1A1A1A] mt-2 text-sm font-semibold">See where you and the other person align — and where you struggle. Plus get a full natal chart for yourself.</p>
              <Link to="/zodiac" data-testid="response-zodiac-cta"
                className="mt-5 inline-flex px-6 py-3 rounded-full bg-[#FF1A82] text-white font-bold hover:brightness-110 transition items-center gap-2">
                Open Zodiac &amp; Compatibility <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}
      </section>
    </PageBg>
  );
}
